import React, { useMemo } from 'react';
import {
  useMaterialReactTable,
  MRT_TableContainer,
} from 'material-react-table';
import { InputTextarea } from 'primereact/inputtextarea';

const DragPerformanceTable = ({ performanceData, onReorder, formik, checklistValues, onChecklistChange }) => {

  const renderTextAreaFull = (fieldName, row) => {
      const name = `performance.${row.index}.${fieldName}`;
      const value = formik.values.performance[row.index][fieldName] || ''; // Fallback to empty string
      
      return (
        <InputTextarea
            name={name}
            value={value}
            onChange={(e) => formik.setFieldValue(name, e.target.value)}
            autoResize
            rows={1}
            style={{
                width: '100%',
                boxSizing: 'border-box',
                border: 'none',
                outline: 'none',
                fontSize: '14px',
                color: '#495057',
                paddingTop: '8px',
                padding: '4px',
                backgroundColor: 'transparent',
                fontFamily: 'inherit'
            }}
        />
      );
  };

  const columns = useMemo(
    () => [
        {
          accessorKey: 'category',
          header: 'Role Deliverables',
          size: 150,
          enableColumnActions: false,
          muiTableHeadCellProps: {
             style: {
                backgroundColor: '#dddddd',
                color: 'black',
                fontWeight: 700,
                textAlign: 'center',
                border: "1px solid #E9ECEF",
            },
          },
          muiTableCellProps: {
            style: {
                textAlign: 'left',
                border: "1px solid white",
                backgroundColor: 'white',
                borderLeft: "2px solid #E9ECEF",
                padding: 0,
            },
          },
          Cell: ({ row }) => renderTextAreaFull('category', row)
        },
        {
          accessorKey: 'standard',
          header: 'Performance Standard',
          size: 200,
          enableColumnActions: false,
          muiTableHeadCellProps: {
             style: {
                backgroundColor: '#dddddd',
                color: 'black',
                fontWeight: 700,
                textAlign: 'center',
                border: "1px solid #E9ECEF",
            },
          },
          muiTableCellProps: {
            style: {
                textAlign: 'left',
                border: "1px solid white",
                backgroundColor: 'white',
                borderLeft: "2px solid #E9ECEF",
                padding: 0,
            },
          },
          Cell: ({ row }) => renderTextAreaFull('standard', row)
        },
        {
          accessorKey: 'deadline',
          header: 'Deadline',
          size: 150,
          enableColumnActions: false,
          muiTableHeadCellProps: {
             style: {
                backgroundColor: '#dddddd',
                color: 'black',
                fontWeight: 700,
                textAlign: 'center',
                border: "1px solid #E9ECEF",
            },
          },
          muiTableCellProps: {
            style: {
                textAlign: 'center',
                border: "1px solid white",
                backgroundColor: 'white',
                borderLeft: "2px solid #E9ECEF",
                padding: 0,
            },
          },
          Cell: ({ row }) => renderTextAreaFull('deadline', row)
        },
        {
            id: 'yes',
            header: 'Yes',
            size: 60,
            enableColumnActions: false,
            muiTableHeadCellProps: {
                align: 'center',
                style: {
                   backgroundColor: '#dddddd',
                   color: 'black',
                   fontWeight: 700,
                   textAlign: 'center',
                   border: "1px solid #E9ECEF",
               },
               sx: {
                    justifyContent: 'center',
                    '& .Mui-TableHeadCell-Content': {
                        justifyContent: 'center',
                    }
               }
             },
             muiTableCellProps: ({ row }) => ({
                style: {
                    textAlign: 'center',
                    border: "1px solid white",
                    borderLeft: "2px solid #E9ECEF",
                    backgroundColor: checklistValues[row.index] === 'yes' ? '#e8f5e9' : 'white',
                    padding: 0,
                    verticalAlign: 'middle'
                },
              }),
            Cell: ({ row }) => (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                     <input 
                        type="checkbox" 
                        checked={checklistValues[row.index] === 'yes'}
                        onChange={() => onChecklistChange(row.index, 'yes')}
                        style={{ width: '24px', height: '24px', cursor: 'pointer', accentColor: '#2e7d32' }}
                    />
                </div>
            )
        },
        {
            id: 'no',
            header: 'No',
            size: 60,
            enableColumnActions: false,
            muiTableHeadCellProps: {
                align: 'center',
                style: {
                   backgroundColor: '#dddddd',
                   color: 'black',
                   fontWeight: 700,
                   textAlign: 'center',
                   border: "1px solid #E9ECEF",
               },
               sx: {
                    justifyContent: 'center',
                    '& .Mui-TableHeadCell-Content': {
                        justifyContent: 'center',
                    }
               }
             },
             muiTableCellProps: ({ row }) => ({
                style: {
                    textAlign: 'center',
                    border: "1px solid white",
                    borderLeft: "2px solid #E9ECEF",
                    backgroundColor: checklistValues[row.index] === 'no' ? '#ffebee' : 'white',
                    padding: 0,
                    verticalAlign: 'middle'
                },
              }),
            Cell: ({ row }) => (
                 <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                     <input 
                        type="checkbox" 
                        checked={checklistValues[row.index] === 'no'}
                        onChange={() => onChecklistChange(row.index, 'no')}
                        style={{ width: '24px', height: '24px', cursor: 'pointer', accentColor: '#d32f2f' }}
                    />
                </div>
            )
        }
      ],
      [checklistValues, formik.values.performance] 
    );

  const table = useMaterialReactTable({
    autoResetPageIndex: false,
    columns,
    data: performanceData || [],
    enableRowOrdering: true,
    enableSorting: false,
    enablePagination: false,
    enableBottomToolbar: false, 
    enableTopToolbar: false,
    enableColumnActions: false,
    muiTableHeadRowProps: {
        style: {
            boxShadow: 'none'
        }
    },
    muiRowDragHandleProps: ({ table }) => ({
      onDragEnd: () => {
        const { draggingRow, hoveredRow } = table.getState();
        if (hoveredRow && draggingRow) {
            onReorder(draggingRow.index, hoveredRow.index);
        }
      },
    }),
    muiTableBodyRowProps: ({ row }) => ({
        hover: false,
        sx: {
            backgroundColor: (row.index === 0 || row.index === 1) ? '#fff8e1' : 'white', // Light amber background for priority rows
            borderBottom: (row.index === 1) ? '2px solid #ffc107' : undefined, // Optional border after 2nd row
        }
    }),
    renderTopToolbar: false,
    initialState: { density: 'compact' }, 
  });

  return <MRT_TableContainer table={table} />;
};

export default DragPerformanceTable;